import { useEffect, useRef, useState } from "react";
import { Mail, Check, X, AlertCircle, Loader2 } from "lucide-react";

import { subscribe } from "../../lib/walletEvents";
import { resendVerification } from "../../api/auth.api";
import useDialogDismiss from "../../hooks/useDialogDismiss";

// Globally-mounted modal that opens whenever the axios interceptor sees a
// 403 with body.error="email_not_verified". This is the trial-abuse gate
// at the wallet-debit layer: user has credits but hasn't clicked the
// verify link, so paid features (AI Suggest, AI Tag, Sonnet chat,
// marketplace pack buy) refuse to charge them. The modal shows a clear
// CTA to resend the verification email.
export default function EmailNotVerifiedModal() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const panelRef = useRef(null);
  const close = () => setOpen(false);
  useDialogDismiss({ open, onClose: close, ref: panelRef });

  useEffect(() => {
    return subscribe("email_not_verified", (payload) => {
      setData(payload);
      setOpen(true);
      setSent(false);
      setErr("");
    });
  }, []);

  const handleResend = async () => {
    if (!data?.email) {
      setErr("We don't have your email on file — please sign out and back in.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      await resendVerification(data.email);
      setSent(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not send. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (!open || !data) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Email verification required"
        className="bg-bg-primary border border-border-default p-8 w-full max-w-md rounded-xl shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-3 right-3 text-text-secondary hover:text-text-primary"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-button-primary/15 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-button-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-text-primary">
              Verify your email to continue
            </h3>
            <p className="text-sm text-text-secondary mt-1">
              {data.message ||
                "Please verify your email address before using paid features."}
            </p>
            {data.email && (
              <p className="text-sm text-text-primary mt-3">
                Verification link sent to:{" "}
                <span className="font-medium">{data.email}</span>
              </p>
            )}
          </div>
        </div>

        <div className="mt-5 text-sm text-text-secondary">
          Check your inbox (and spam folder) for the link, or request a new
          one below. Once verified, return here and try again — no need to
          re-enter anything.
        </div>

        {sent && (
          <div className="mt-4 bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 rounded-lg p-3 text-sm flex items-start gap-2">
            <Check className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              A new verification email is on its way. The previous link is
              now invalid.
            </span>
          </div>
        )}
        {err && (
          <div className="mt-4 bg-rose-500/10 border border-rose-500/40 text-rose-200 rounded-lg p-3 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <div className="mt-6 flex gap-2 justify-end">
          <button
            onClick={() => setOpen(false)}
            className="px-4 py-2 rounded-md text-sm text-text-secondary hover:bg-bg-secondary/40"
          >
            Close
          </button>
          <button
            onClick={handleResend}
            disabled={busy || sent}
            className="px-4 py-2 rounded-md text-sm bg-button-primary text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending…
              </>
            ) : sent ? (
              "Sent"
            ) : (
              "Resend verification email"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
